import React, { useState, useEffect, useCallback } from 'react';
import { lndAPI } from '../../../services';
import { GraduationCap, BookOpen, CheckCircle, Clock, Award, Play, ChevronRight, X, ArrowLeft } from 'lucide-react';

const STATUS_COLORS = { not_started:'bg-gray-100 text-gray-600', in_progress:'bg-blue-100 text-blue-700', completed:'bg-green-100 text-green-700' };
const STATUS_LABELS = { not_started:'Not Started', in_progress:'In Progress', completed:'Completed' };
const CAT_LABELS = { technical:'Technical', leadership:'Leadership', compliance:'Compliance', soft_skills:'Soft Skills', safety:'Safety', onboarding:'Onboarding', sales:'Sales', hr_policy:'HR Policy', other:'Other' };

const MyLearningPage = () => {
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('courses');
  const [activeCourse, setActiveCourse] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [lessonIdx, setLessonIdx] = useState(0);
  const [kcAnswer, setKcAnswer] = useState(null);
  const [kcResult, setKcResult] = useState(null);
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessAnswers, setAssessAnswers] = useState([]);
  const [assessResult, setAssessResult] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cr, cert] = await Promise.all([lndAPI.getMyCourses(), lndAPI.getMyCertificates()]);
      setCourses(Array.isArray(cr?.data) ? cr.data : []);
      setCertificates(Array.isArray(cert?.data) ? cert.data : []);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCourse = async (course) => {
    try {
      const res = await lndAPI.getCourseForEmployee(course._id);
      setCourseDetail(res?.data);
      setActiveCourse(course);
      const completed = res?.data?.progress?.lessonsCompleted || [];
      const nextLesson = course.lessons?.findIndex((_, i) => !completed.includes(i));
      setLessonIdx(nextLesson >= 0 ? nextLesson : 0);
      setKcAnswer(null); setKcResult(null); setShowAssessment(false); setAssessResult(null);
    } catch { showToast('Failed to open course', 'error'); }
  };

  const handleCompleteLesson = async () => {
    try {
      await lndAPI.completeLesson(activeCourse._id, lessonIdx);
      const updated = await lndAPI.getCourseForEmployee(activeCourse._id);
      setCourseDetail(updated?.data);
      setKcAnswer(null); setKcResult(null);
      const totalLessons = activeCourse.lessons?.length || 0;
      if (lessonIdx + 1 >= totalLessons) {
        setShowAssessment(true);
        setAssessAnswers(new Array(updated?.data?.course?.assessment?.questions?.length || 0).fill(null));
      } else {
        setLessonIdx(i => i + 1);
      }
    } catch { showToast('Failed to mark lesson', 'error'); }
  };

  const handleSubmitAssessment = async () => {
    try {
      const res = await lndAPI.submitAssessment(activeCourse._id, assessAnswers);
      setAssessResult(res?.data);
      load();
    } catch { showToast('Failed to submit assessment', 'error'); }
  };

  const lesson = activeCourse?.lessons?.[lessonIdx];
  const progress = courseDetail?.progress;
  const lessonsCompleted = progress?.lessonsCompleted || [];
  const allLessonsDone = activeCourse && lessonsCompleted.length >= (activeCourse.lessons?.length || 0);

  if (activeCourse) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}
        <button onClick={() => setActiveCourse(null)} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-4 text-sm"><ArrowLeft className="w-4 h-4"/>Back to Courses</button>
        <h2 className="text-xl font-bold text-gray-900 mb-1">{activeCourse.title}</h2>
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-6">
          <span>{activeCourse.lessons?.length} lessons</span>·
          <span>{activeCourse.durationHours}h</span>·
          <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[progress?.status || 'not_started']}`}>{STATUS_LABELS[progress?.status || 'not_started']}</span>
        </div>

        {/* Progress bar */}
        <div className="bg-gray-200 rounded-full h-2 mb-6">
          <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width:`${activeCourse.lessons?.length ? (lessonsCompleted.length/activeCourse.lessons.length)*100 : 0}%` }}/>
        </div>

        {/* Assessment result */}
        {assessResult && (
          <div className={`rounded-xl p-6 text-center mb-6 ${assessResult.passed?'bg-green-50 border border-green-200':'bg-red-50 border border-red-200'}`}>
            <p className="text-2xl font-bold mb-1">{assessResult.score}%</p>
            <p className={`font-semibold ${assessResult.passed?'text-green-700':'text-red-600'}`}>{assessResult.passed?'🎉 Passed! Certificate generated.':'❌ Failed. Please retake.'}</p>
            {assessResult.passed && <button onClick={() => setActiveCourse(null)} className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm">View Certificates</button>}
            {!assessResult.passed && <button onClick={() => { setAssessResult(null); setAssessAnswers(new Array(courseDetail?.course?.assessment?.questions?.length||0).fill(null)); }} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm">Retake</button>}
          </div>
        )}

        {/* Final Assessment */}
        {showAssessment && !assessResult && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Final Assessment</h3>
            {(courseDetail?.course?.assessment?.questions || []).map((q, qi) => (
              <div key={qi} className="mb-5">
                <p className="text-sm font-medium text-gray-800 mb-2">{qi+1}. {q.question}</p>
                {q.options?.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer mb-1 ${assessAnswers[qi]===oi?'bg-blue-50 border border-blue-300':'hover:bg-gray-50'}`}>
                    <input type="radio" checked={assessAnswers[qi]===oi} onChange={() => { const a=[...assessAnswers]; a[qi]=oi; setAssessAnswers(a); }} className="w-4 h-4"/>
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            ))}
            <button onClick={handleSubmitAssessment} disabled={assessAnswers.some(a=>a===null)} className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">Submit Assessment</button>
          </div>
        )}

        {/* Lesson Player */}
        {!showAssessment && !assessResult && lesson && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-gray-400">Lesson {lessonIdx+1} of {activeCourse.lessons?.length}</span>
              {lessonsCompleted.includes(lessonIdx) && <CheckCircle className="w-4 h-4 text-green-500"/>}
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-3">{lesson.title}</h3>

            {lesson.videoUrl && (
              <div className="mb-4 bg-black rounded-lg overflow-hidden aspect-video">
                <iframe src={lesson.videoUrl} className="w-full h-full" allowFullScreen title={lesson.title}/>
              </div>
            )}
            {lesson.content && <p className="text-gray-600 text-sm mb-4 leading-relaxed">{lesson.content}</p>}

            {/* Knowledge Check */}
            {lesson.knowledgeCheck?.question && !lessonsCompleted.includes(lessonIdx) && (
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-semibold text-blue-900 mb-3">Knowledge Check</p>
                <p className="text-sm text-gray-800 mb-3">{lesson.knowledgeCheck.question}</p>
                {lesson.knowledgeCheck.options?.map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer mb-1 border ${kcAnswer===oi?'bg-white border-blue-400':'bg-white border-gray-200 hover:border-blue-200'} ${kcResult!==null?(oi===lesson.knowledgeCheck.correctAnswer?'border-green-500 bg-green-50':kcAnswer===oi?'border-red-400 bg-red-50':''):''}}`}>
                    <input type="radio" disabled={kcResult!==null} checked={kcAnswer===oi} onChange={() => setKcAnswer(oi)} className="w-3 h-3"/>
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
                {kcResult === null && kcAnswer !== null && (
                  <button onClick={() => setKcResult(kcAnswer === lesson.knowledgeCheck.correctAnswer)} className="mt-2 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium">Check Answer</button>
                )}
                {kcResult !== null && (
                  <div className={`mt-2 p-2 rounded text-xs ${kcResult?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>
                    {kcResult ? '✓ Correct!' : `✗ Incorrect. ${lesson.knowledgeCheck.explanation || 'Try again.'}`}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              {lessonIdx > 0 && <button onClick={() => { setLessonIdx(i=>i-1); setKcAnswer(null); setKcResult(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Previous</button>}
              {!lessonsCompleted.includes(lessonIdx) && (
                <button onClick={handleCompleteLesson} disabled={lesson.knowledgeCheck?.question && kcResult !== true} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {lessonIdx+1 === activeCourse.lessons?.length ? 'Complete & Take Assessment' : 'Complete Lesson'}
                </button>
              )}
              {lessonsCompleted.includes(lessonIdx) && lessonIdx+1 < activeCourse.lessons?.length && (
                <button onClick={() => { setLessonIdx(i=>i+1); setKcAnswer(null); setKcResult(null); }} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Next Lesson <ChevronRight className="w-4 h-4 inline"/></button>
              )}
              {lessonsCompleted.includes(lessonIdx) && lessonIdx+1 === activeCourse.lessons?.length && !showAssessment && (
                <button onClick={() => { setShowAssessment(true); setAssessAnswers(new Array(courseDetail?.course?.assessment?.questions?.length||0).fill(null)); }} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">Take Final Assessment</button>
              )}
            </div>

            {/* Lesson list */}
            <div className="mt-5 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 mb-2">All Lessons</p>
              {activeCourse.lessons?.map((l, i) => (
                <button key={i} onClick={() => { setLessonIdx(i); setKcAnswer(null); setKcResult(null); }} className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg mb-1 text-sm ${i===lessonIdx?'bg-blue-50 text-blue-700':'hover:bg-gray-50 text-gray-700'}`}>
                  {lessonsCompleted.includes(i) ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0"/> : <BookOpen className="w-4 h-4 text-gray-400 flex-shrink-0"/>}
                  {l.title || `Lesson ${i+1}`}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><GraduationCap className="w-6 h-6 text-blue-600"/>My Learning</h1>
          <p className="text-sm text-gray-500 mt-1">Your assigned courses and certificates</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label:'Assigned', value: courses.length, icon:BookOpen, color:'text-blue-600', bg:'bg-blue-50' },
          { label:'Completed', value: courses.filter(c=>c.progress?.status==='completed').length, icon:CheckCircle, color:'text-green-600', bg:'bg-green-50' },
          { label:'Certificates', value: certificates.length, icon:Award, color:'text-yellow-600', bg:'bg-yellow-50' }
        ].map(({ label, value, icon:Icon, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
            <Icon className={`w-8 h-8 ${color}`}/><div><p className="text-xs text-gray-500">{label}</p><p className={`text-xl font-bold ${color}`}>{value}</p></div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {[['courses','My Courses'],['certificates','Certificates']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-medium ${tab===k?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>{l}</button>
        ))}
      </div>

      {tab === 'courses' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {loading ? <p className="col-span-2 text-center py-10 text-gray-400">Loading...</p>
          : courses.length === 0 ? (
            <div className="col-span-2 text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">
              <GraduationCap className="w-10 h-10 mb-2 mx-auto opacity-30"/>No courses assigned
            </div>
          ) : courses.map(c => (
            <div key={c._id} className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all" onClick={() => openCourse(c)}>
              <div className="flex items-start justify-between mb-2">
                <p className="font-semibold text-gray-900">{c.title}</p>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.courseType==='mandatory'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-600'}`}>{c.courseType==='mandatory'?'Mandatory':'Optional'}</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">{CAT_LABELS[c.category]} · {c.lessons?.length} lessons · {c.durationHours}h</p>
              <div className="flex items-center justify-between">
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[c.progress?.status||'not_started']}`}>{STATUS_LABELS[c.progress?.status||'not_started']}</span>
                <div className="flex items-center gap-1 text-blue-600 text-xs font-medium"><Play className="w-3 h-3"/>{c.progress?.status==='completed'?'Review':'Start'}</div>
              </div>
              <div className="mt-3 bg-gray-200 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-blue-500 transition-all" style={{ width:`${c.lessons?.length?(c.progress?.lessonsCompleted?.length||0)/c.lessons.length*100:0}%` }}/>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'certificates' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {certificates.length === 0 ? (
            <div className="col-span-2 text-center py-12 text-gray-400 bg-white rounded-xl border border-gray-200">
              <Award className="w-10 h-10 mb-2 mx-auto opacity-30"/>No certificates yet. Complete courses to earn them.
            </div>
          ) : certificates.map(cert => (
            <div key={cert._id} className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2"><Award className="w-6 h-6 text-yellow-600"/><p className="font-semibold text-gray-900">{cert.course?.title}</p></div>
              <p className="text-xs text-gray-500">Certificate No: {cert.certificateNumber}</p>
              <p className="text-xs text-gray-500">Score: {cert.score}%</p>
              <p className="text-xs text-gray-500">Issued: {new Date(cert.issuedAt).toLocaleDateString('en-IN')}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyLearningPage;
